import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Content,
  Footer,
  Header,
  Heading,
  Input,
  Modal,
  Placeholder,
  Secondary,
  Stack,
  Strong,
  Text,
  Toolbar,
  ToolbarItem,
} from '@kinesis/bungle';
import type { ScopeRuleAxis } from '@/types';
import { SCOPE_RULE_AXES, ScopeRuleAxisDef } from '@/data/scope-rules';

// Cards are the picker — clicking a card picks that axis and confirms (no
// separate primary action). Styled as a native button so focus/hover/aria are
// handled by the browser. Ported from suit's `inventory-scope-control`
// add-rule modal.
const CardButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 12px;
  background: #fff;
  border: 1px solid #d0d7de;
  border-radius: 4px;
  cursor: pointer;
  font: inherit;
  color: inherit;

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }
`;

const ScrollList = styled.div`
  max-height: 420px;
  overflow-y: auto;
`;

type AddRuleModalProps = {
  animation?: boolean;
  onClose: () => void;
  // Fired with the chosen axis when the user clicks a card. The parent appends
  // a skeleton rule of that axis to the scope list and closes the modal.
  onSelect: (axis: ScopeRuleAxis) => void;
};

const matches = (axis: ScopeRuleAxisDef, query: string): boolean => {
  if (query === '') return true;
  const needle = query.toLowerCase();
  return (
    axis.label.toLowerCase().includes(needle) ||
    axis.description.toLowerCase().includes(needle) ||
    axis.example.toLowerCase().includes(needle)
  );
};

const AddRuleModal = ({ animation, onClose, onSelect }: AddRuleModalProps) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () => SCOPE_RULE_AXES.filter((axis) => matches(axis, search)),
    [search],
  );

  const handleSelect = useCallback(
    (axis: ScopeRuleAxis) => () => {
      onSelect(axis);
    },
    [onSelect],
  );

  return (
    <Modal
      animation={animation}
      aria-label='Add scope rule'
      onClose={onClose}
      width={560}
    >
      <Header border>
        <Toolbar>
          <ToolbarItem ellipsify sizing='fill-container'>
            <Heading as='h2' size='large'>
              Add a scope rule
            </Heading>
          </ToolbarItem>
        </Toolbar>
      </Header>
      <Content padding='medium'>
        <Stack space='medium'>
          <Text>
            Choose the kind of rule to add to this scenario. Each rule trims
            what flows into the scenario&rsquo;s totals.
          </Text>
          <Input
            type='search'
            placeholder='Search rule types'
            value={search}
            onChange={setSearch}
            autoFocus
            autoComplete='off'
          />
          <ScrollList>
            <Stack space='small'>
              {filtered.map((axis) => (
                <CardButton
                  key={axis.id}
                  type='button'
                  onClick={handleSelect(axis.id)}
                  aria-label={axis.label}
                >
                  <Stack space='xsmall'>
                    <Text>
                      <Strong>{axis.label}</Strong>
                    </Text>
                    <Text>{axis.description}</Text>
                    <Secondary>
                      <Text>{axis.example}</Text>
                    </Secondary>
                  </Stack>
                </CardButton>
              ))}
              {filtered.length === 0 && (
                <Placeholder>No rule types match your search.</Placeholder>
              )}
            </Stack>
          </ScrollList>
        </Stack>
      </Content>
      <Footer border>
        <Toolbar>
          <ToolbarItem>
            <Button onClick={onClose} variant='secondary'>
              Cancel
            </Button>
          </ToolbarItem>
        </Toolbar>
      </Footer>
    </Modal>
  );
};

export { AddRuleModal };
export type { AddRuleModalProps };
